import axios, { type AxiosInstance } from 'axios'
import { router } from '@/router'

/**
 * The one client for `/admin/v1`.
 *
 * `withCredentials` plus `withXSRFToken` is Sanctum's SPA flow: the session cookie is httpOnly and
 * the CSRF token travels in a header, so a token stolen from storage is not a thing that can
 * exist.
 *
 * A 401 anywhere means the session went away — an idle timeout, or a sign-out in another tab —
 * and the only sensible response is to stop showing data that is no longer ours to show.
 */
export const api: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE,
  withCredentials: true,
  withXSRFToken: true,
  headers: { Accept: 'application/json' },
})

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error?.response?.status

    if (status === 401 && router.currentRoute.value.name !== 'login') {
      await router.push({ name: 'login', query: { next: router.currentRoute.value.fullPath } })
    }

    // The role requires two-factor and it is not set up: the security page is the only way on.
    if (
      status === 403
      && error?.response?.data?.error?.code === 'totp_enrolment_required'
      && router.currentRoute.value.name !== 'security'
    ) {
      await router.push({ name: 'security' })
    }

    return Promise.reject(error)
  },
)

/** The API's own Persian message when it has one — it knows why better than the screen can guess. */
export function apiMessage(error: unknown, fallback: string): string {
  const response = (error as { response?: { data?: { error?: { message?: string } } } })?.response

  return response?.data?.error?.message ?? fallback
}

/** Field errors from a 422, for showing next to the input that caused them. */
export function apiErrors(error: unknown): Record<string, string[]> {
  return (error as { response?: { data?: { errors?: Record<string, string[]> } } })?.response?.data?.errors ?? {}
}
